/**
 * PitcherChat - AI Chat interface for pitch tipping intelligence
 * Uses Gemini 2.5 Flash with full pitcher context for answering questions
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Zap, ChevronDown, ChevronUp, MessageSquare, X } from 'lucide-react';
import { NODE_BACKEND_URL } from '../../utils/constant';
import { marked } from 'marked';

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Markdown renderer (content is from our own AI backend, so it's trusted)
const renderMarkdown = (text) => {
  if (!text) return '';
  return marked.parse(text);
};

// Build comprehensive system prompt with pitcher tipping data
const buildSystemPrompt = (pitcherData) => {
  if (!pitcherData) return '';

  const {
    pitcher,
    team,
    threatAssessment,
    primaryTell,
    secondaryTells,
    atBatStrategy,
    situationalKeys,
    pitchScouting,
    exploitationMatrix,
  } = pitcherData;

  // Format pitch scouting data
  const pitchBreakdown = pitchScouting
    ? Object.entries(pitchScouting)
        .map(([type, data]) => `- **${type}**: ${data.totalSeen} pitches, ${(data.tippingRate * 100).toFixed(0)}% tipping rate`)
        .join('\n')
    : 'No pitch breakdown available';

  // Format secondary tells
  const secondaryTellsList = secondaryTells
    ? secondaryTells.map(t => `- ${t.pitchRevealed}: ${t.whatToLookFor}`).join('\n')
    : 'None identified';

  // Format exploitation matrix
  const exploitData = exploitationMatrix
    ? exploitationMatrix.slice(0, 5).map(e =>
        `- ${e.categoryLabel}: ${e.pitchRevealed} (${(e.frequency * 100).toFixed(0)}% freq, ${e.reliability})`
      ).join('\n')
    : 'No exploitation data';

  return `You are "THE WIRE" - an elite advanced scouting intelligence assistant for baseball hitters and coaches. You have COMPLETE ACCESS to detailed analysis of ${pitcher}'s tipping patterns.

---

## PITCHER PROFILE: ${pitcher}
**Team:** ${team || 'Unknown'}
**Threat Level:** ${threatAssessment?.level || 'UNKNOWN'}
**Overall Tipping Rate:** ${threatAssessment?.tippingRate ? (threatAssessment.tippingRate * 100).toFixed(1) + '%' : 'N/A'}
**Total Pitches Analyzed:** ${threatAssessment?.totalAnalyzed || 0}

---

## THREAT ASSESSMENT

**HEADLINE:** ${threatAssessment?.headline || 'No headline available'}

**QUICK READ:**
${threatAssessment?.quickRead || 'No quick read available'}

---

## PRIMARY TELL

**What to Look For:** ${primaryTell?.whatToLookFor || 'Not identified'}
**When It Happens:** ${primaryTell?.whenItHappens || 'Unknown'}
**Pitch Revealed:** ${primaryTell?.pitchRevealed || 'Unknown'}
**Confidence:** ${primaryTell?.confidence || 'Unknown'}

---

## SECONDARY TELLS

${secondaryTellsList}

---

## PITCH-BY-PITCH BREAKDOWN

${pitchBreakdown}

---

## AT-BAT STRATEGY

**First Pitch:** ${atBatStrategy?.firstPitch || 'Standard approach'}

**Ahead in Count:** ${atBatStrategy?.aheadInCount || 'Hunt your pitch'}

**Behind in Count:** ${atBatStrategy?.behindInCount || 'Stay disciplined'}

**Two Strikes:** ${atBatStrategy?.twoStrikes || 'Compete'}

---

## SITUATIONAL KEYS

**From the Stretch:** ${situationalKeys?.stretch || 'No specific notes'}

**Under Pressure:** ${situationalKeys?.pressure || 'No specific notes'}

---

## EXPLOITATION MATRIX

${exploitData}

---

## YOUR ROLE

You are the hitter's secret weapon. Answer questions about ${pitcher}'s:
- Tipping tells and how to spot them
- Best approach for different counts
- Which pitches are most exploitable
- Game-specific strategies
- How to communicate tells to teammates

Be concise but actionable. Every answer should help the hitter gain an edge.

"See the tell. Know the pitch. Own the at-bat."`;
};

// Call Gemini API
const callGeminiAPI = async (userMessage, conversationHistory, pitcherData) => {
  try {
    const systemPrompt = buildSystemPrompt(pitcherData);

    // Build messages array for the API
    const messages = [
      { role: 'user', content: systemPrompt },
      { role: 'assistant', content: `I understand. I have complete intelligence on ${pitcherData?.pitcher || 'this pitcher'}'s tipping patterns. I can help you identify tells, plan at-bats, and develop exploitation strategies. What would you like to know?` },
      ...conversationHistory.filter(m => m.role !== 'system').map(m => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content
      })),
      { role: 'user', content: userMessage }
    ];

    const response = await fetch(`${NODE_BACKEND_URL}/api/chat/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messages,
        options: {
          model: 'gemini-2.5-flash',
          temperature: 0.7,
          sport: 'baseball',
          teamId: 'tigers',
          teamName: 'Advanced Scouting Intel',
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.content || data.response || data.message || data.text || 'I apologize, but I could not generate a response. Please try again.';
  } catch (error) {
    console.error('Gemini API error:', error);
    return generateFallbackResponse(userMessage, pitcherData);
  }
};

// Fallback response generator
const generateFallbackResponse = (question, pitcherData) => {
  const q = question.toLowerCase();
  const pitcher = pitcherData?.pitcher || 'this pitcher';
  const tippingRate = pitcherData?.threatAssessment?.tippingRate;
  const headline = pitcherData?.threatAssessment?.headline;
  const primaryTell = pitcherData?.primaryTell;

  if (q.includes('tell') || q.includes('tip')) {
    return primaryTell
      ? `**Primary Tell for ${pitcher}:**\n\n${primaryTell.whatToLookFor}\n\nThis happens ${primaryTell.whenItHappens} and reveals the ${primaryTell.pitchRevealed}. Confidence: ${primaryTell.confidence}`
      : `The primary tell for ${pitcher} involves pre-pitch hand movements. Watch for any pump, tap, or head-touch before the leg lift.`;
  }

  if (q.includes('approach') || q.includes('strategy') || q.includes('first')) {
    return pitcherData?.atBatStrategy?.firstPitch
      ? `**First Pitch Approach:**\n\n${pitcherData.atBatStrategy.firstPitch}`
      : `Trust the tell. If you see pre-pitch movement, sit on off-speed. If hands stay quiet, gear up for the fastball.`;
  }

  if (q.includes('ahead') || q.includes('hitter count')) {
    return pitcherData?.atBatStrategy?.aheadInCount
      ? `**When Ahead in Count:**\n\n${pitcherData.atBatStrategy.aheadInCount}`
      : 'Hunt your pitch with confidence. Use the tell to eliminate guesswork.';
  }

  if (q.includes('two strike') || q.includes('2 strike')) {
    return pitcherData?.atBatStrategy?.twoStrikes
      ? `**Two-Strike Approach:**\n\n${pitcherData.atBatStrategy.twoStrikes}`
      : 'The tell is your lifeline. Use it to eliminate one pitch from your mind and focus on competing.';
  }

  // Default response
  return headline
    ? `**${pitcher} Intelligence Summary:**\n\n${headline}\n\nOverall tipping rate: ${tippingRate ? (tippingRate * 100).toFixed(0) + '%' : 'High'}`
    : `${pitcher} shows consistent tipping patterns that can be exploited. Focus on pre-pitch hand movements to identify pitch type early.`;
};

const PitcherChat = ({
  pitcherData,
  pitcherName,
  isOpen = false,
  onToggle,
  className = '',
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize welcome message when pitcher changes
  useEffect(() => {
    if (pitcherData) {
      const tippingPct = pitcherData.threatAssessment?.tippingRate
        ? (pitcherData.threatAssessment.tippingRate * 100).toFixed(0)
        : '?';

      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: `**THE WIRE** - Advanced Scouting Intelligence\n\n**Pitcher:** ${pitcherData.pitcher || pitcherName}\n**Tipping Rate:** ${tippingPct}%\n**Threat Level:** ${pitcherData.threatAssessment?.level || 'UNKNOWN'}\n\nI have complete access to this pitcher's tipping patterns, tells, and exploitation strategies.\n\nPowered by Gemini 2.5 Flash. What would you like to know?`,
      }]);
    }
  }, [pitcherData, pitcherName]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessageText = input.trim();
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessageText,
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await callGeminiAPI(userMessageText, messages, pitcherData);
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'I encountered an error processing your request. Please try again.',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Suggested questions - pitcher-specific
  const suggestions = [
    `What are ${pitcherName || 'his'}'s main tells?`,
    'How should I approach the first pitch?',
    'What pitch type is most exploitable?',
    'Two-strike strategy against him?',
  ];

  // Minimized button
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/40 rounded-xl text-orange-400 hover:bg-orange-500/30 transition-colors ${className}`}
      >
        <MessageSquare className="w-4 h-4" />
        <span className="text-sm font-medium">Ask AI</span>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] bg-zinc-900/95 backdrop-blur-xl border border-zinc-700 rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[500px] ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-800/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <Zap className="w-4 h-4 text-orange-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Pitch Intel Chat</h3>
            <p className="text-zinc-500 text-xs">{pitcherName || 'Pitcher Analysis'}</p>
          </div>
        </div>
        <button
          onClick={onToggle}
          className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-zinc-400" />
        </button>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role !== 'user' && (
              <div className="w-7 h-7 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <Bot className="w-3.5 h-3.5 text-orange-400" />
              </div>
            )}
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                message.role === 'user'
                  ? 'bg-orange-500 text-white'
                  : 'bg-zinc-800 text-zinc-100'
              } prose prose-sm prose-invert max-w-none [&_p]:mb-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_strong]:text-orange-300 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_code]:bg-zinc-700 [&_code]:px-1 [&_code]:rounded`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
            />
            {message.role === 'user' && (
              <div className="w-7 h-7 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-zinc-300" />
              </div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-orange-400" />
            </div>
            <div className="bg-zinc-800 px-3 py-2 rounded-xl">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInput(suggestion);
                  inputRef.current?.focus();
                }}
                className="px-2.5 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-lg transition-colors border border-zinc-700"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-zinc-800">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about tipping tells..."
            className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-orange-500/50"
            disabled={isTyping}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg transition-colors"
          >
            {isTyping ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PitcherChat;
